/**
 * App container.
 * @module components/theme/App/App
 */

import { Component, useState } from 'react';
import jwtDecode from 'jwt-decode';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { asyncConnect, Helmet } from '@plone/volto/helpers';
import { Segment } from 'semantic-ui-react';
import { renderRoutes } from 'react-router-config';
import { Slide, ToastContainer, toast } from 'react-toastify';
import split from 'lodash/split';
import join from 'lodash/join';
import trim from 'lodash/trim';
import cx from 'classnames';
import config from '@plone/volto/registry';
import { PluggablesProvider } from '@plone/volto/components/manage/Pluggable';
import { visitBlocks } from '@plone/volto/helpers/Blocks/Blocks';
import { injectIntl, defineMessages } from 'react-intl';
import { ParallaxProvider } from 'react-scroll-parallax';
import Error from '@plone/volto/error';
import {
  Breadcrumbs,
  Footer,
  Header,
  Icon,
  OutdatedBrowser,
  AppExtras,
  SkipLinks,
} from '@plone/volto/components';
import { BodyClass, getBaseUrl, getView, isCmsUi } from '@plone/volto/helpers';
import {
  getBreadcrumbs,
  getContent,
  getNavigation,
  getTypes,
  getWorkflow,
} from '@plone/volto/actions';

import clearSVG from '@plone/volto/icons/clear.svg';
import MultilingualRedirector from '@plone/volto/components/theme/MultilingualRedirector/MultilingualRedirector';
import WorkingCopyToastsFactory from '@plone/volto/components/manage/WorkingCopyToastsFactory/WorkingCopyToastsFactory';
import LockingToastsFactory from '@plone/volto/components/manage/LockingToastsFactory/LockingToastsFactory';
import PrivacyPolicyProvider from 'volto-fzj-internet/components/manage/PrivacyPolicy/PrivacyPolicyProvider';


import * as Sentry from '@sentry/browser';

import AnchorLink from 'react-anchor-link-smooth-scroll';
import Matomo from 'volto-fzj-internet/components/Matomo/Matomo';
import ETrackerScript from 'volto-fzj-internet/components/etracker/ETracker';

const messages = defineMessages({
  scrollTop: {
    id: 'scroll to top',
    defaultMessage: 'scroll to top',
  },
});


/**
 * @export
 * @class App
 * @extends {Component}
 */
class App extends Component {
  /**
   * Property types.
   * @property {Object} propTypes Property types.
   * @static
   */
  static propTypes = {
    pathname: PropTypes.string.isRequired,
  };

  state = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  /**
   * @method componentWillReceiveProps
   * @param {Object} nextProps Next properties
   * @returns {undefined}
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.pathname !== this.props.pathname) {
      if (this.state.hasError) {
        this.setState({ hasError: false });
      }
    }
  }

  /**
   * ComponentDidCatch
   * @method ComponentDidCatch
   * @param {string} error  The error
   * @param {string} info The info
   * @returns {undefined}
   */
  componentDidCatch(error, info) {
    this.setState({ hasError: true, error, errorInfo: info });
    if (__CLIENT__) {
      if (window?.env?.RAZZLE_SENTRY_DSN || __SENTRY__?.SENTRY_DSN) {
        Sentry.captureException(error);
      }
    }
  }

  /**
   * Render method.
   * @method render
   * @returns {string} Markup for the component.
   */
  render() {
    const { views } = config;
    const path = getBaseUrl(this.props.pathname);
    const action = getView(this.props.pathname);
    const isCmsUI = isCmsUi(this.props.pathname);
    const ConnectionRefusedView = views.errorViews.ECONNREFUSED;

    const language =
      this.props.content?.language?.token ?? this.props.intl?.locale;
    // START CUSTOMIZATION
    const scrollToTop = () => {
      const scrollProgress = document.getElementById('progress');
      const position = document.documentElement.scrollTop;
      const calcHeight =
        document.documentElement.scrollHeight -
        document.documentElement.clientHeight;
      const scrollValue = Math.round((position * 100) / calcHeight);
      scrollProgress.style.background = `conic-gradient(#023d6b ${scrollValue}%, #fff ${scrollValue}%)`;

    };

    if (__CLIENT__) {
      window.onscroll = scrollToTop;
    }
    // END CUSTOMIZATION

    return (
      <>
        {!config.settings.isFZJProject && (path.includes('/de/karriere') || path.includes('/en/career')) && <ETrackerScript />}
        <PluggablesProvider>
          <ParallaxProvider>
            <PrivacyPolicyProvider>
              {language && (
                <Helmet>
                  <html lang={language} />
                </Helmet>
              )}
              <BodyClass className={`view-${action}view`} />
              {(this.props.pathname === '/de' || this.props.pathname === '/en') && (
                <header className="h1-bitv">
                  <h1>{this.props.content?.title}</h1>
                </header>
              )}
              {/* Body class depending on content type */}
              {this.props.content && this.props.content['@type'] && (
                <BodyClass
                  className={`contenttype-${this.props.content['@type']
                    .replace(' ', '-')
                    .toLowerCase()}`}
                />
              )}

              {/* Body class depending on sections */}
              <BodyClass
                className={cx({
                  [trim(join(split(this.props.pathname, '/'), ' section-'))]:
                    this.props.pathname !== '/',
                  siteroot: this.props.pathname === '/',
                  'is-authenticated': !!this.props.token,
                  'is-anonymous': !this.props.token,
                  'cms-ui': isCmsUI,
                  'public-ui': !isCmsUI,
                })}
              />
              <SkipLinks />
              <Header pathname={path} />
              <Breadcrumbs pathname={path} />
              <MultilingualRedirector
                pathname={this.props.pathname}
                contentLanguage={this.props.content?.language?.token}
              >
                <Segment basic className="content-area">
                  <main>
                    <OutdatedBrowser />
                    {this.props.connectionRefused ? (
                      <ConnectionRefusedView />
                    ) : this.state.hasError ? (
                      <Error
                        message={this.state.error.message}
                        stackTrace={this.state.errorInfo.componentStack}
                      />
                    ) : (
                      renderRoutes(this.props.route.routes, {
                        staticContext: this.props.staticContext,
                      })
                    )}
                  </main>
                </Segment>
              </MultilingualRedirector>
              <Footer />
              {/* START CUSTOMIZATION */}
              <div role="complementary">
                <AnchorLink
                  aria-label={this.props.intl.formatMessage(messages.scrollTop)}
                  id="progress"
                  className="progress-wrapper"
                  href="#main"
                >
                  <svg
                    className="scroll-to-top"
                    viewBox="0 0 36 36"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      id="triangle"
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M24.707 19.293L23.293 20.707L18 15.414L12.707 20.707L11.293 19.293L18 12.586L24.707 19.293Z"
                    />
                  </svg>
                </AnchorLink>
              </div>
              {/* END CUSTOMIZATION */}
              <LockingToastsFactory
                content={this.props.content}
                user={this.props.userId}
              />
              <WorkingCopyToastsFactory content={this.props.content} />
              <ToastContainer
                position={toast.POSITION.BOTTOM_CENTER}
                hideProgressBar
                transition={Slide}
                autoClose={5000}
                closeButton={
                  <Icon
                    className="toast-dismiss-action"
                    name={clearSVG}
                    size="18px"
                  />
                }
              />
              <AppExtras {...this.props} />
            </PrivacyPolicyProvider>
          </ParallaxProvider>
        </PluggablesProvider>
        <Matomo />
      </>
    );
  }
}

export const __test__ = connect(
  (state, props) => ({
    pathname: props.location.pathname,
    token: state.userSession.token,
    content: state.content.data,
    apiError: state.apierror.error,
    connectionRefused: state.apierror.connectionRefused,
  }),
  {},
)(App);

export const fetchContent = async ({ store, location }) => {
  const content = await store.dispatch(
    getContent(getBaseUrl(location.pathname)),
  );

  const promises = [];
  const { blocksConfig } = config.blocks;

  const visitor = ([id, data]) => {
    const blockType = data['@type'];
    const { getAsyncData } = blocksConfig[blockType];
    if (getAsyncData) {
      const p = getAsyncData({
        store,
        dispatch: store.dispatch,
        path: location.pathname,
        location,
        id,
        data,
      });
      if (!p?.length) {
        throw new Error(
          'You should return a list of promises from getAsyncData',
        );
      }
      promises.push(...p);
    }
  };

  visitBlocks(content, visitor);

  await Promise.allSettled(promises);

  return content;
};

export default compose(
  asyncConnect([
    {
      key: 'breadcrumbs',
      promise: ({ location, store: { dispatch } }) =>
        __SERVER__ && dispatch(getBreadcrumbs(getBaseUrl(location.pathname))),
    },
    {
      key: 'content',
      promise: ({ location, store }) =>
        __SERVER__ && fetchContent({ store, location }),
    },
    {
      key: 'navigation',
      promise: ({ location, store: { dispatch } }) =>
        __SERVER__ &&
        dispatch(
          getNavigation(
            getBaseUrl(location.pathname),
            config.settings.navDepth,
          ),
        ),
    },
    {
      key: 'types',
      promise: ({ location, store: { dispatch } }) =>
        __SERVER__ && dispatch(getTypes(getBaseUrl(location.pathname))),
    },
    {
      key: 'workflow',
      promise: ({ location, store: { dispatch } }) =>
        __SERVER__ && dispatch(getWorkflow(getBaseUrl(location.pathname))),
    },
  ]),
  injectIntl,
  connect(
    (state, props) => ({
      pathname: props.location.pathname,
      token: state.userSession.token,
      userId: state.userSession.token
        ? jwtDecode(state.userSession.token).sub
        : '',
      content: state.content.data,
      apiError: state.apierror.error,
      connectionRefused: state.apierror.connectionRefused,
    }),
    null,
  ),
)(App);
